import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import {useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';

import {SchemaFieldSelectorGraphQL} from '../../../../graphql-fragments/schemas/__generated/SchemaFieldSelectorGraphQL';
import {
  FetchSelectorsByIdQuery,
  FetchSelectorsByIdQueryVariables,
} from './__generated/FetchSelectorsByIdQuery';

export type SchemaFieldSelectorsDict = Record<string, SchemaFieldSelectorGraphQL>;

export const FETCH_SELECTORS_BY_ID_QUERY: DocumentNode = gql`
  query FetchSelectorsByIdQuery($includeArchived: StrictBoolean!) @skipAudit @engComponent(name: NOTEBOOK) {
    schemaFieldSelectors(includeArchived: $includeArchived) {
      ...SchemaFieldSelectorGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
`;

export function getSelectorsById(selectors: Array<SchemaFieldSelectorGraphQL>): SchemaFieldSelectorsDict {
  return selectors.reduce((acc, selector) => {
    acc[selector.id] = selector;
    return acc;
  }, {} as SchemaFieldSelectorsDict);
}

export function selectorsAreLoaded(
  selectorsById: SchemaFieldSelectorsDict | AsyncStatus.LOADING | AsyncStatus.ERROR | AsyncStatus.NOT_FETCHED
): selectorsById is SchemaFieldSelectorsDict {
  return (
    selectorsById !== AsyncStatus.LOADING &&
    selectorsById !== AsyncStatus.ERROR &&
    selectorsById !== AsyncStatus.NOT_FETCHED
  );
}

export function convertFetchSelectorsQueryResultToSchemaFieldSelectorsDict(
  data: FetchSelectorsByIdQuery
): SchemaFieldSelectorsDict {
  const schemaFieldSelectors = data.schemaFieldSelectors;

  let selectorsById: SchemaFieldSelectorsDict = {};
  selectorsById = schemaFieldSelectors.reduce((acc, selector) => {
    acc[selector.id] = selector;
    return acc;
  }, selectorsById);
  return selectorsById;
}

/**
 * Query SchemaFieldSelectors and construct a selectorsById mapping for use with notebook objects.
 *
 * If 'ERROR' is returned, it will already be reported, so the caller is only responsible for appropriate
 * rendering.
 */
export default function useSelectorsById({
  includeArchivedSelectors,
  skip,
}: {
  includeArchivedSelectors: boolean;
  skip?: boolean;
}): SchemaFieldSelectorsDict | AsyncStatus.LOADING | AsyncStatus.ERROR | AsyncStatus.NOT_FETCHED {
  const result = useSkippableBenchlingQuery<FetchSelectorsByIdQuery, FetchSelectorsByIdQueryVariables>(
    FETCH_SELECTORS_BY_ID_QUERY,
    {
      variables: {
        includeArchived: includeArchivedSelectors,
      },
      skip,
    }
  );

  // in order to memoize the result, we need to pull the data into a separate variable for hook deps
  const data = result.type === QueryResultType.SUCCESS ? result.data : undefined;
  return useMemo(() => {
    if (result.type === QueryResultType.ERROR) {
      return AsyncStatus.ERROR;
    }
    if (result.type === QueryResultType.PENDING) {
      return AsyncStatus.LOADING;
    }
    if (result.type === QueryResultType.SKIPPED) {
      return AsyncStatus.NOT_FETCHED;
    }
    return convertFetchSelectorsQueryResultToSchemaFieldSelectorsDict(data!);
  }, [data, result.type]);
}
